import { DateFormat, userConfigStore } from "@/store/user-config-store";
import type { TFunction } from "i18next";

interface DateInputOptions {
  dateFormat?: DateFormat;
}

export interface YearMonth {
  year: number;
  month: number;
}

export interface MonthInputValidationOptions extends DateInputOptions {
  pastMonthsDisabled?: boolean;
  futureMonthsDisabled?: boolean;
  minMonth?: YearMonth;
  maxMonth?: YearMonth;
}

interface DateInputValidationOptions extends DateInputOptions {
  requireFutureDate?: boolean;
  disallowPastDate?: boolean;
  disallowFutureDate?: boolean;
  minDate?: Date;
}

export enum DateInputValidationError {
  InvalidFormat = "invalidFormat",
  MustBeInTheFuture = "mustBeInTheFuture",
  CannotBeInThePast = "cannotBeInThePast",
  CannotBeInTheFuture = "cannotBeInTheFuture",
  BeforeMinDate = "beforeMinDate",
}

export enum MonthInputValidationError {
  InvalidFormat = "invalidFormat",
  CannotBeInThePast = "cannotBeInThePast",
  CannotBeInTheFuture = "cannotBeInTheFuture",
  BeforeMinMonth = "beforeMinMonth",
  AfterMaxMonth = "afterMaxMonth",
}

function resolveDateFormat(dateFormat?: DateFormat): DateFormat {
  return dateFormat ?? userConfigStore.getState().dateFormat;
}

export function getMonthInputFormat(dateFormat?: DateFormat): string {
  switch (resolveDateFormat(dateFormat)) {
    case DateFormat.EuropeanDot:
      return "MM.YYYY";
    case DateFormat.EuropeanSlash:
    case DateFormat.US:
      return "MM/YYYY";
    case DateFormat.ISO:
      return "YYYY-MM";
  }
}

function isValidYearMonth(value: YearMonth): boolean {
  return (
    Number.isInteger(value.year) &&
    value.year >= 1000 &&
    value.year <= 9999 &&
    Number.isInteger(value.month) &&
    value.month >= 1 &&
    value.month <= 12
  );
}

function getYearMonthIndex(value: YearMonth): number {
  return value.year * 12 + value.month - 1;
}

export function dateToYearMonth(date = new Date()): YearMonth {
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
}

export function yearMonthToInputString(value: YearMonth | undefined, options: DateInputOptions = {}): string {
  if (!value || !isValidYearMonth(value)) {
    return "";
  }

  const month = String(value.month).padStart(2, "0");

  switch (resolveDateFormat(options.dateFormat)) {
    case DateFormat.EuropeanDot:
      return `${month}.${value.year}`;
    case DateFormat.EuropeanSlash:
    case DateFormat.US:
      return `${month}/${value.year}`;
    case DateFormat.ISO:
      return `${value.year}-${month}`;
  }
}

export function inputStringToYearMonth(value: string, options: DateInputOptions = {}): YearMonth | undefined {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  let year: number | undefined;
  let month: number | undefined;

  switch (resolveDateFormat(options.dateFormat)) {
    case DateFormat.EuropeanDot: {
      const match = trimmedValue.match(/^(\d{1,2})\.(\d{4})$/);
      if (match) {
        month = Number(match[1]);
        year = Number(match[2]);
      }
      break;
    }
    case DateFormat.EuropeanSlash:
    case DateFormat.US: {
      const match = trimmedValue.match(/^(\d{1,2})\/(\d{4})$/);
      if (match) {
        month = Number(match[1]);
        year = Number(match[2]);
      }
      break;
    }
    case DateFormat.ISO: {
      const match = trimmedValue.match(/^(\d{4})-(\d{1,2})$/);
      if (match) {
        year = Number(match[1]);
        month = Number(match[2]);
      }
      break;
    }
  }

  const yearMonth = { year: year ?? 0, month: month ?? 0 };
  return isValidYearMonth(yearMonth) ? yearMonth : undefined;
}

export function yearMonthToApiString(value: YearMonth | undefined): string | undefined {
  if (!value || !isValidYearMonth(value)) {
    return undefined;
  }

  return `${value.year}-${String(value.month).padStart(2, "0")}`;
}

export function apiYearMonthToInputString(value: string | null | undefined, options: DateInputOptions = {}): string {
  if (!value) {
    return "";
  }

  const match = value.match(/^(\d{4})-(0[1-9]|1[0-2])$/);
  return match ? yearMonthToInputString({ year: Number(match[1]), month: Number(match[2]) }, options) : "";
}

export function monthInputStringToApi(value: string, options: DateInputOptions = {}): string | undefined {
  return yearMonthToApiString(inputStringToYearMonth(value, options));
}

export function validateYearMonth(
  value: YearMonth,
  options: MonthInputValidationOptions = {},
): MonthInputValidationError | undefined {
  if (!isValidYearMonth(value)) {
    return MonthInputValidationError.InvalidFormat;
  }

  const valueIndex = getYearMonthIndex(value);
  const currentMonthIndex = getYearMonthIndex(dateToYearMonth());

  if (options.pastMonthsDisabled && valueIndex < currentMonthIndex) {
    return MonthInputValidationError.CannotBeInThePast;
  }

  if (options.futureMonthsDisabled && valueIndex > currentMonthIndex) {
    return MonthInputValidationError.CannotBeInTheFuture;
  }

  if (options.minMonth && valueIndex < getYearMonthIndex(options.minMonth)) {
    return MonthInputValidationError.BeforeMinMonth;
  }

  if (options.maxMonth && valueIndex > getYearMonthIndex(options.maxMonth)) {
    return MonthInputValidationError.AfterMaxMonth;
  }

  return undefined;
}

export function validateMonthInputString(
  value: string,
  options: MonthInputValidationOptions = {},
): MonthInputValidationError | undefined {
  if (!value.trim()) {
    return undefined;
  }

  const yearMonth = inputStringToYearMonth(value, options);
  return yearMonth ? validateYearMonth(yearMonth, options) : MonthInputValidationError.InvalidFormat;
}

export function getMonthInputValidationMessage(error: MonthInputValidationError): string {
  switch (error) {
    case MonthInputValidationError.CannotBeInThePast:
      return JSON.stringify({ key: "common:MonthCannotBeInThePast" });
    case MonthInputValidationError.CannotBeInTheFuture:
      return JSON.stringify({ key: "common:MonthCannotBeInTheFuture" });
    case MonthInputValidationError.BeforeMinMonth:
      return JSON.stringify({ key: "common:MonthIsBeforeMinimum" });
    case MonthInputValidationError.AfterMaxMonth:
      return JSON.stringify({ key: "common:MonthIsAfterMaximum" });
    case MonthInputValidationError.InvalidFormat:
    default:
      return JSON.stringify({
        key: "common:InvalidDateFormat",
        options: { format: getMonthInputFormat() },
      });
  }
}

export function getStartOfToday(referenceDate = new Date()): Date {
  return new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
}

export function getStartOfTomorrow(referenceDate = new Date()): Date {
  const tomorrow = getStartOfToday(referenceDate);
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow;
}

export function getEndOfToday(referenceDate = new Date()): Date {
  const endOfToday = getStartOfTomorrow(referenceDate);
  endOfToday.setMilliseconds(endOfToday.getMilliseconds() - 1);
  return endOfToday;
}

export function dateToInputString(date: Date | undefined, options: DateInputOptions = {}): string {
  if (!date || Number.isNaN(date.getTime())) {
    return "";
  }

  const dateFormat = resolveDateFormat(options.dateFormat);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = String(date.getFullYear());

  switch (dateFormat) {
    case DateFormat.EuropeanSlash:
      return `${day}/${month}/${year}`;
    case DateFormat.US:
      return `${month}/${day}/${year}`;
    case DateFormat.ISO:
      return `${year}-${month}-${day}`;
    case DateFormat.EuropeanDot:
    default:
      return `${day}.${month}.${year}`;
  }
}

export function parseApiDateOnly(date?: string | null): Date | undefined {
  if (!date) {
    return undefined;
  }

  const dateOnly = date.match(/^(\d{4}-\d{2}-\d{2})(?:T.*)?$/)?.[1];

  return dateOnly ? inputStringToDate(dateOnly, { dateFormat: DateFormat.ISO }) : undefined;
}

export function inputStringToDate(value: string, options: DateInputOptions = {}): Date | undefined {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return undefined;
  }

  const dateFormat = resolveDateFormat(options.dateFormat);

  let day: number | undefined;
  let month: number | undefined;
  let year: number | undefined;

  switch (dateFormat) {
    case DateFormat.EuropeanDot: {
      const match = trimmedValue.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);

      if (match) {
        day = Number(match[1]);
        month = Number(match[2]);
        year = Number(match[3]);
      }

      break;
    }
    case DateFormat.EuropeanSlash: {
      const match = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

      if (match) {
        day = Number(match[1]);
        month = Number(match[2]);
        year = Number(match[3]);
      }

      break;
    }
    case DateFormat.US: {
      const match = trimmedValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

      if (match) {
        month = Number(match[1]);
        day = Number(match[2]);
        year = Number(match[3]);
      }

      break;
    }
    case DateFormat.ISO: {
      const match = trimmedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);

      if (match) {
        year = Number(match[1]);
        month = Number(match[2]);
        day = Number(match[3]);
      }

      break;
    }
  }

  if (day === undefined || month === undefined || year === undefined) {
    return undefined;
  }

  const date = new Date(year, month - 1, day);

  if (
    Number.isNaN(date.getTime()) ||
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    return undefined;
  }

  return date;
}

export function validateDateInputString(
  value: string,
  options: DateInputValidationOptions = {},
): DateInputValidationError | undefined {
  const trimmedValue = value.trim();

  if (!trimmedValue) {
    return undefined;
  }

  const date = inputStringToDate(trimmedValue, options);

  if (!date) {
    return DateInputValidationError.InvalidFormat;
  }

  if (options.requireFutureDate && date < getStartOfTomorrow()) {
    return DateInputValidationError.MustBeInTheFuture;
  }

  if (options.disallowPastDate && date < getStartOfToday()) {
    return DateInputValidationError.CannotBeInThePast;
  }

  if (options.disallowFutureDate && date > getEndOfToday()) {
    return DateInputValidationError.CannotBeInTheFuture;
  }

  if (options.minDate && date < getStartOfToday(options.minDate)) {
    return DateInputValidationError.BeforeMinDate;
  }

  return undefined;
}

export interface DateInputValidationMessageDescriptor {
  key: string;
  options?: Record<string, string>;
}

export function getDateInputValidationMessageDescriptor(
  error: DateInputValidationError,
): DateInputValidationMessageDescriptor {
  switch (error) {
    case DateInputValidationError.MustBeInTheFuture:
      return { key: "common:DateMustBeInTheFuture" };
    case DateInputValidationError.CannotBeInThePast:
      return { key: "common:DateCannotBeInThePast" };
    case DateInputValidationError.CannotBeInTheFuture:
      return { key: "common:DateCannotBeInTheFuture" };
    case DateInputValidationError.BeforeMinDate:
      return { key: "common:DateIsTooFarInThePast" };
    case DateInputValidationError.InvalidFormat:
    default:
      return {
        key: "common:InvalidDateFormat",
        options: { format: userConfigStore.getState().dateFormat },
      };
  }
}

export function getDateInputValidationMessage(error: DateInputValidationError): string {
  return JSON.stringify(getDateInputValidationMessageDescriptor(error));
}

export function translateDateInputValidationError(error: DateInputValidationError, t: TFunction): string {
  const { key, options } = getDateInputValidationMessageDescriptor(error);
  return t(key, options);
}
